/**
 * Arabic for the Asset Management suite — the register, condition and risk,
 * maintenance plans, and lifecycle and capital plan. Proposed; pending native
 * aviation review.
 *
 * الصحة (health) is the platform's combined score and is kept apart from
 * الحالة (condition), the surveyed one, exactly as the screens keep them
 * apart. The labels rendered through `t(variable)` — stages, bands, depreciation
 * methods, plan states — are here too, since the extractor cannot see them.
 */
const ar: Record<string, string> = {
  "The asset register with the airport's facilities, buildings, landside and equipment; their condition, risk and inspections; preventive maintenance; lifecycle and renewal cost; and the documents.":
    "سجل الأصول مع مرافق المطار ومبانيه والجانب البري والمعدات؛ وحالتها وخطورتها وفحوصها؛ والصيانة الوقائية؛ ودورة الحياة وتكلفة التجديد؛ والوثائق.",
  "The file is empty.": "الملف فارغ.",
  "There is no 'tag' column.": "لا يوجد عمود 'tag'.",
  "Line {n}: no tag.": "السطر {n}: لا يوجد رمز.",
  'Line {n}: {col} "{v}" is not a number.': 'السطر {n}: القيمة "{v}" في {col} ليست رقماً.',
  'Line {n}: {col} "{v}" is not a date (YYYY-MM-DD).': 'السطر {n}: القيمة "{v}" في {col} ليست تاريخاً (YYYY-MM-DD).',
  'Line {n}: lifecycle_stage "{v}" is not one of {list}.': 'السطر {n}: المرحلة "{v}" ليست من بين {list}.',
  "{a} of {n} assets are assessed; {u} have no condition, defect, age or forecast recorded.":
    "تم تقييم {a} من {n} أصلاً؛ و{u} لا يُسجَّل لها حالة أو عيب أو عمر أو تنبؤ.",
  "{h} assessed assets are high risk (15 or more of 25).": "{h} من الأصول المقيَّمة عالية الخطورة (15 أو أكثر من 25).",
  "Highest risk: {tag}, criticality {c}, health {score}.": "الأعلى خطورة: {tag}، الأهمية {c}، الصحة {score}.",
  "No assessed asset is at risk.": "لا يوجد أصل مقيَّم معرَّض للخطر.",
  "What is highest risk?": "ما الأعلى خطورة؟",
  "Which types are in the worst condition?": "أي الأنواع في أسوأ حالة؟",
  "{site} · {suite}": "{site} · {suite}",
  "Recalculating…": "جارٍ إعادة الحساب…",
  "Recalculate health": "إعادة حساب الصحة",
  Assessed: "مقيَّمة",
  "of {n} assets": "من {n} أصلاً",
  "Mean health": "متوسط الصحة",
  "0–100, assessed assets only": "من 0 إلى 100، للأصول المقيَّمة فقط",
  "Poor or very poor": "ضعيفة أو ضعيفة جداً",
  "High risk": "عالية الخطورة",
  "risk 15 or more of 25": "خطورة 15 أو أكثر من 25",
  "have enough recorded to assess": "لديها ما يكفي من البيانات للتقييم",
  "Criticality × health": "الأهمية × الصحة",
  "Each cell counts the assets there; color is the risk.": "كل خانة تعدّ الأصول فيها؛ واللون يدل على الخطورة.",
  "Health by asset type": "الصحة حسب نوع الأصل",
  "Worst first; unassessed types last.": "الأسوأ أولاً؛ والأنواع غير المقيَّمة أخيراً.",
  "Highest risk": "الأعلى خطورة",
  "Criticality × how far from very good, then the lowest health.": 'الأهمية × البعد عن "جيد جداً"، ثم الأدنى صحة.',
  Criticality: "الأهمية",
  Health: "الصحة",
  Risk: "الخطورة",
  "No asset is assessed yet, so no risk can be ranked.": "لم يُقيَّم أي أصل بعد، فلا يمكن ترتيب الخطورة.",
  "Mean health by month": "متوسط الصحة حسب الشهر",
  "Assessed assets, last twelve months.": "الأصول المقيَّمة، آخر اثني عشر شهراً.",
  "How it is worked out:": "طريقة الحساب:",
  "Criticality {c}, {band}: {n} assets, risk {r}": "الأهمية {c}، {band}: {n} أصلاً، الخطورة {r}",
  "Not in the matrix: {n} unassessed assets.": "خارج المصفوفة: {n} أصلاً غير مقيَّم.",
  "{tag} — {name}, {type}, criticality {c} of 5.": "{tag} — {name}، {type}، الأهمية {c} من 5.",
  "Health {score} ({band}).": "الصحة {score} ({band}).",
  "Health not assessed.": "لم تُقيَّم الصحة.",
  "Why is its health this?": "لماذا صحته بهذا المستوى؟",
  "Life remaining": "العمر المتبقي",
  years: "سنوات",
  "{p}% of a {life}-year life used": "استُهلك {p}% من عمر قدره {life} سنة",
  "Needs an install date and a design life": "يحتاج تاريخ تركيب وعمراً تصميمياً",
  "Failures, 24 months": "الأعطال خلال 24 شهراً",
  "MTBF {mtbf} days · MTTR {mttr} h": "متوسط الوقت بين الأعطال {mtbf} يوماً · متوسط وقت الإصلاح {mttr} ساعة",
  "No corrective work recorded": "لا توجد أعمال تصحيحية مسجلة",
  "Maintenance cost to date": "تكلفة الصيانة حتى الآن",
  "Register and lifecycle": "السجل ودورة الحياة",
  Stage: "المرحلة",
  "Serial number": "الرقم التسلسلي",
  Installed: "تاريخ التركيب",
  Purchased: "تاريخ الشراء",
  "Purchase cost": "تكلفة الشراء",
  "Replacement value": "قيمة الاستبدال",
  "Design life": "العمر التصميمي",
  "End of life": "نهاية العمر",
  "Book value": "القيمة الدفترية",
  "Warranty until": "الضمان حتى",
  "Health and why": "الصحة وأسبابها",
  "Condition, less open defects, age past 60% of life and predicted failure.":
    "الحالة، مطروحاً منها العيوب المفتوحة، والعمر بعد 60% من العمر التصميمي، والعطل المتوقع.",
  "Not assessed: no condition survey, defect, age or prediction is recorded for this asset.":
    "غير مقيَّم: لا يوجد مسح حالة أو عيب أو عمر أو تنبؤ مسجل لهذا الأصل.",
  "Surveyed condition": "الحالة وفق المسح",
  "{n} open, worst severity {w}, −{p}": "{n} مفتوحة، أشد خطورة {w}، −{p}",
  "{p}% of design life, −{a}": "{p}% من العمر التصميمي، −{a}",
  "Predicted failure": "العطل المتوقع",
  "{p}% probability, −{a}": "احتمال {p}%، −{a}",
  "Health over time": "الصحة عبر الزمن",
  Components: "المكوّنات",
  "Assets fitted inside this one.": "أصول مركّبة داخل هذا الأصل.",
  "Every change is written to the audit log with who made it.": "يُسجَّل كل تغيير في سجل التدقيق مع اسم من أجراه.",
  "Work raised against this asset, newest first.": "الأعمال المفتوحة على هذا الأصل، الأحدث أولاً.",
  Cost: "التكلفة",
  "No work order is recorded against this asset.": "لا يوجد أمر عمل مسجل على هذا الأصل.",
  Finding: "الملاحظة",
  Found: "تاريخ الرصد",
  "No defect is recorded against this asset.": "لا يوجد عيب مسجل على هذا الأصل.",
  "Maintenance plans": "خطط الصيانة",
  "Preventive work raised automatically when due.": "أعمال وقائية تُفتح تلقائياً عند استحقاقها.",
  "No maintenance plan covers this asset.": "لا توجد خطة صيانة تغطي هذا الأصل.",
  "Add a plan": "إضافة خطة",
  "A calendar plan on this asset.": "خطة زمنية على هذا الأصل.",
  "Tasks, one per line": "المهام، مهمة في كل سطر",
  "Add the plan": "أضف الخطة",
  "Latest readings": "أحدث القراءات",
  "No reading is recorded for this asset.": "لا توجد قراءة مسجلة لهذا الأصل.",
  Forecasts: "التنبؤات",
  "{p}% failure probability": "احتمال عطل {p}%",
  "about {n} days of life left": "نحو {n} يوماً متبقية من العمر",
  "No model has scored this asset.": "لم يقيّم أي نموذج هذا الأصل.",
  "No document is linked to this asset.": "لا توجد وثيقة مرتبطة بهذا الأصل.",
  "{n} assets are past their design life; renewing this year's backlog costs {cost}.":
    "{n} أصلاً تجاوزت عمرها التصميمي؛ وتجديد متأخرات هذا العام يكلف {cost}.",
  "Replacement value on record: {v} across {n} assets; book value {b}.":
    "قيمة الاستبدال المسجلة: {v} على {n} أصلاً؛ والقيمة الدفترية {b}.",
  "{f} of {n} assets can be forecast (install date and design life known).":
    "يمكن التنبؤ لـ {f} من {n} أصلاً (تاريخ التركيب والعمر التصميمي معروفان).",
  "What needs renewing this year?": "ما الذي يحتاج تجديداً هذا العام؟",
  "Is the capital plan funded?": "هل الخطة الرأسمالية ممولة؟",
  "{n} assets valued": "{n} أصلاً مقيَّم القيمة",
  "{n} assets with a cost, date and life": "{n} أصلاً لها تكلفة وتاريخ وعمر",
  "Past end of life": "تجاوزت نهاية العمر",
  "Warranties ending in 90 days": "ضمانات تنتهي خلال 90 يوماً",
  "{n} in warranty": "{n} ضمن الضمان",
  "have an install or purchase date": "لها تاريخ تركيب أو شراء",
  "have a design life (asset or type policy)": "لها عمر تصميمي (من الأصل أو سياسة النوع)",
  "have a replacement value (asset or type policy)": "لها قيمة استبدال (من الأصل أو سياسة النوع)",
  "Renewal cost by year": "تكلفة التجديد حسب السنة",
  "Replacement cost of the assets reaching end of life each year; the backlog is in the first year.":
    "تكلفة استبدال الأصول التي تبلغ نهاية عمرها كل عام؛ والمتأخرات في السنة الأولى.",
  "Nothing can be forecast yet: no asset has both an install date and a design life. Import the dates, or set a type policy.":
    "لا يمكن التنبؤ بعد: لا يوجد أصل له تاريخ تركيب وعمر تصميمي معاً. استورد التواريخ أو حدّد سياسة للنوع.",
  "Capex envelope for the year": "مخصص الإنفاق الرأسمالي للسنة",
  "Of them critical": "منها حرجة",
  "Renewal cost": "تكلفة التجديد",
  "Capex envelope": "مخصص الإنفاق الرأسمالي",
  "Not costed": "غير مسعّرة",
  "The renewal cost exceeds the capex envelope in {years}.": "تتجاوز تكلفة التجديد مخصص الإنفاق الرأسمالي في {years}.",
  "No asset has an end of life or a replacement value yet.": "لا يوجد أصل له نهاية عمر أو قيمة استبدال بعد.",
  "By end of life": "حسب نهاية العمر",
  "Soonest first; past end of life at the top.": "الأقرب أولاً؛ والمتجاوزة نهاية عمرها في الأعلى.",
  Life: "العمر",
  Replacement: "الاستبدال",
  "Asset-type policies": "سياسات أنواع الأصول",
  "A design life and replacement cost per type, used wherever an asset has none of its own — and labeled 'policy' where used.":
    "عمر تصميمي وتكلفة استبدال لكل نوع، تُستخدم حيث لا يملك الأصل قيمه الخاصة — وتُوسم بـ'سياسة' حيث تُستخدم.",
  "No asset types on this site.": "لا توجد أنواع أصول في هذا الموقع.",
  "Design life (years)": "العمر التصميمي (سنوات)",
  "Replacement cost each (SAR)": "تكلفة الاستبدال للوحدة (ريال)",
  "Design life for {type}": "العمر التصميمي لـ {type}",
  "Replacement cost for {type}": "تكلفة الاستبدال لـ {type}",
  "{a} in warranty · {b} expired": "{a} ضمن الضمان · {b} منتهية",
  "Days left": "الأيام المتبقية",
  "No warranty ends in the next 90 days.": "لا ينتهي أي ضمان خلال الأيام التسعين القادمة.",
  "{n} active plans: {o} overdue, {d} due within their lead time, {m} due in the next 30 days.":
    "{n} خطة نشطة: {o} متأخرة، و{d} مستحقة ضمن مهلتها، و{m} مستحقة خلال 30 يوماً.",
  "PM compliance over 12 months is {p}%.": "الالتزام بالصيانة الوقائية خلال 12 شهراً {p}%.",
  "No preventive work has come due in the last 12 months.": "لم تستحق أي أعمال وقائية خلال آخر 12 شهراً.",
  "What maintenance is overdue?": "ما الصيانة المتأخرة؟",
  "Which assets fail most?": "أي الأصول تتعطل أكثر؟",
  "Create due work orders now": "افتح أوامر العمل المستحقة الآن",
  "{n} work orders raised as drafts: {list}": "فُتح {n} أمر عمل كمسودات: {list}",
  "Nothing was due that did not already have an open work order.": "لا يوجد مستحق دون أمر عمل مفتوح بالفعل.",
  "Active plans": "الخطط النشطة",
  "Due in 30 days": "مستحقة خلال 30 يوماً",
  "PM compliance, 12 months": "الالتزام بالصيانة الوقائية، 12 شهراً",
  "{a} of {b} done by their due date": "أُنجز {a} من {b} في موعده",
  "Planned share of work": "نسبة الأعمال المخططة",
  "preventive, predictive and inspection ÷ all work": "الوقائية والتنبؤية والتفتيش ÷ جميع الأعمال",
  "work orders in 24 months name their asset": "أوامر عمل خلال 24 شهراً تحدد أصلها",
  "Overdue first, then by due date.": "المتأخرة أولاً، ثم حسب تاريخ الاستحقاق.",
  "Last done": "آخر تنفيذ",
  "No maintenance plan yet. Add one below, or on an asset's own page.":
    "لا توجد خطة صيانة بعد. أضف خطة أدناه أو من صفحة الأصل.",
  "Plan a whole asset type": "خطة لنوع أصل كامل",
  "The same plan on every asset of one type that does not already have it.":
    "الخطة نفسها على كل أصل من نوع واحد لا يملكها بعد.",
  "Choose a type": "اختر نوعاً",
  "e.g. Quarterly service": "مثال: خدمة ربع سنوية",
  "Plan {n} assets": "خطط لـ {n} أصلاً",
  "Plan the type": "خطط للنوع",
  "{n} plans created.": "أُنشئت {n} خطة.",
  "Assets that fail most": "الأصول الأكثر تعطلاً",
  "Corrective work orders in 24 months, with mean time between failures and to repair.":
    "أوامر العمل التصحيحية خلال 24 شهراً، مع متوسط الوقت بين الأعطال ومتوسط وقت الإصلاح.",
  "MTBF (days)": "متوسط الوقت بين الأعطال (أيام)",
  "MTTR (hours)": "متوسط وقت الإصلاح (ساعات)",
  "No corrective work order names its asset yet, so no failure rate can be worked out.":
    "لا يوجد أمر عمل تصحيحي يحدد أصله بعد، فلا يمكن حساب معدل الأعطال.",
  "No work orders in the last 12 months.": "لا توجد أوامر عمل خلال آخر 12 شهراً.",
  "Work orders by kind, 12 months": "أوامر العمل حسب النوع، 12 شهراً",
  "Cost by kind, 12 months": "التكلفة حسب النوع، 12 شهراً",
  "{n} assets across {types} types and {zones} zones.": "{n} أصلاً عبر {types} نوعاً و{zones} منطقة.",
  "{dated} have an install date; {assessed} have a health score; {components} are components of another asset.":
    "{dated} لها تاريخ تركيب؛ و{assessed} لها درجة صحة؛ و{components} مكوّنات لأصل آخر.",
  "Which assets are critical?": "أي الأصول حرجة؟",
  "What is missing from the register?": "ما الناقص في السجل؟",
  "Import lifecycle data": "استيراد بيانات دورة الحياة",
  "Asset types": "أنواع الأصول",
  Zones: "المناطق",
  "assets fitted inside another asset": "أصول مركّبة داخل أصل آخر",
  "have a health score": "لها درجة صحة",
  "have a manufacturer recorded": "مسجل لها الشركة المصنعة",
  "Choose a zone to see what is in it and below it.": "اختر منطقة لترى ما فيها وما تحتها.",
  "Showing {n} of {total}; narrow the filter to see the rest.": "عرض {n} من {total}؛ ضيّق التصفية لرؤية الباقي.",
  "{n} assets": "{n} أصلاً",
  "Tag, name or serial": "الرمز أو الاسم أو الرقم التسلسلي",
  "All types": "كل الأنواع",
  "All stages": "كل المراحل",
  "Clear filters": "مسح التصفية",
  "No asset matches this filter.": "لا يوجد أصل يطابق هذه التصفية.",
  component: "مكوّن",
  "{n} components": "{n} مكوّنات",
  "The first {n} are listed; search or filter to find the others.": "تُعرض أول {n}؛ ابحث أو صفِّ للعثور على البقية.",
  "This site has no zones yet.": "لا توجد مناطق في هذا الموقع بعد.",
  "Whole site": "الموقع بالكامل",
  "A CSV with a header row; assets are matched by tag and nothing new is created.":
    "ملف CSV بصف عناوين؛ تُطابَق الأصول بالرمز ولا يُنشأ شيء جديد.",
  "Columns understood:": "الأعمدة المفهومة:",
  "Dates as YYYY-MM-DD.": "التواريخ بصيغة YYYY-MM-DD.",
  "CSV to import": "ملف CSV للاستيراد",
  "{n} rows ready.": "{n} صفاً جاهزاً.",
  "Ignored columns: {list}.": "أعمدة متجاهلة: {list}.",
  "Importing…": "جارٍ الاستيراد…",
  "Import {n} rows": "استيراد {n} صفاً",
  "{n} assets updated.": "حُدِّث {n} أصلاً.",
  "{n} tags not found: {list}": "{n} رمزاً غير موجود: {list}",
  "{n} of {of}": "{n} من {of}",
  "Criticality {n} of 5": "الأهمية {n} من 5",
  "From the asset-type policy, not recorded on this asset": "من سياسة نوع الأصل، وليس مسجلاً على هذا الأصل",
  policy: "سياسة",
  "Asset register": "سجل الأصول",
  "Condition and risk": "الحالة والخطورة",
  "Lifecycle and capital plan": "دورة الحياة والخطة الرأسمالية",
  "Asset Management": "إدارة الأصول",
  "The asset register as a hierarchy, the condition and risk of every asset, preventive maintenance plans, and each asset's lifecycle and renewal cost.":
    "سجل الأصول كتسلسل هرمي، وحالة كل أصل وخطورته، وخطط الصيانة الوقائية، ودورة حياة كل أصل وتكلفة تجديده.",
  "Every asset on the aerodrome in one register, placed in its zone and, where it is a component, inside the asset it belongs to. Each asset carries a health score built from its surveyed condition, open defects, age against its design life and any failure forecast, and a risk that weighs that health by how critical the asset is. Preventive maintenance plans raise their own work orders when due, and the suite measures whether they are done on time and which assets fail most. The lifecycle view counts what each asset is worth, when it reaches the end of its life and what replacing it will cost, year by year against the capital envelope — and says plainly how much of the register those numbers stand on.":
    "كل أصل في المطار في سجل واحد، موضوع في منطقته، وإن كان مكوّناً ففي الأصل الذي ينتمي إليه. لكل أصل درجة صحة مبنية على حالته وفق المسح، وعيوبه المفتوحة، وعمره مقارنة بعمره التصميمي، وأي تنبؤ بعطل، وخطورة تزن تلك الصحة بمدى أهمية الأصل. تفتح خطط الصيانة الوقائية أوامر عملها عند استحقاقها، وتقيس المجموعة هل تُنجز في موعدها وأي الأصول تتعطل أكثر. ويحسب عرض دورة الحياة قيمة كل أصل ومتى يبلغ نهاية عمره وتكلفة استبداله، سنة بسنة مقابل المخصص الرأسمالي — ويبيّن بوضوح على كم من السجل تقوم هذه الأرقام.",
  "Every asset by zone and hierarchy, searchable and filterable, each opening to its record, health, work, plans and documents; lifecycle data imported by tag from a spreadsheet.":
    "كل أصل حسب المنطقة والتسلسل الهرمي، قابل للبحث والتصفية، ويفتح كل منها على سجله وصحته وأعماله وخططه ووثائقه؛ مع استيراد بيانات دورة الحياة بالرمز من جدول بيانات.",
  "Assets placed by criticality against health, the highest risks first, health by asset type and month, and a count of the assets not yet assessed.":
    "الأصول موزعة حسب الأهمية مقابل الصحة، والأعلى خطورة أولاً، والصحة حسب نوع الأصل والشهر، وعدد الأصول غير المقيَّمة بعد.",
  "Preventive plans with what is overdue and due, compliance over twelve months, planned against unplanned work, and the assets that fail most with their MTBF and MTTR.":
    "الخطط الوقائية مع المتأخر والمستحق، والالتزام خلال اثني عشر شهراً، والأعمال المخططة مقابل غير المخططة، والأصول الأكثر تعطلاً مع متوسط الوقت بين الأعطال ومتوسط وقت الإصلاح.",
  "Age, remaining life, book and replacement value, a ten-year renewal forecast against the capital envelope, design-life policies per asset type, and warranties ending soon.":
    "العمر والعمر المتبقي والقيمة الدفترية وقيمة الاستبدال، وتنبؤ تجديد لعشر سنوات مقابل المخصص الرأسمالي، وسياسات العمر التصميمي لكل نوع أصل، والضمانات التي تنتهي قريباً.",
  "In service": "في الخدمة",
  Standby: "احتياطي",
  Decommissioned: "خارج التشغيل",
  Disposed: "مُستبعد",
  "Straight line": "القسط الثابت",
  "Declining balance": "الرصيد المتناقص",
  "Not depreciated": "غير مستهلك",
  Good: "جيد",
  "Waiting for usage": "بانتظار الاستخدام",
  Paused: "موقوفة مؤقتاً",
  Triaged: "مُصنَّفة",
  Resolved: "مُعالجة",
  "Lifecycle stage": "مرحلة دورة الحياة",
  "Purchase cost (SAR)": "تكلفة الشراء (ريال)",
  "Replacement value (SAR)": "قيمة الاستبدال (ريال)",
  Depreciation: "الإهلاك",
  "Residual value (%)": "القيمة المتبقية (%)",
  "Every (days)": "كل (أيام)",
  "First due": "أول استحقاق",
  "Raise this many days ahead": "افتح قبل هذا العدد من الأيام",
  "Work and defects": "الأعمال والعيوب",
  "Readings and documents": "القراءات والوثائق",
  "Failures by asset": "الأعطال حسب الأصل",
  "Planned and unplanned work": "الأعمال المخططة وغير المخططة",
  Active: "نشطة",
  "Renewal forecast": "تنبؤ التجديد",
  "Type policies": "سياسات الأنواع",
  Warranties: "الضمانات",
  "Asset sections": "أقسام الأصل",
  "Maintenance views": "عروض الصيانة",
  "Which plans": "أي الخطط",
  "Lifecycle views": "عروض دورة الحياة",
  /* registering, editing, history and notes */
  "Required for this type.": "مطلوب لهذا النوع.",
  "Must be a number.": "يجب أن يكون رقماً.",
  "At least {n}.": "على الأقل {n}.",
  "At most {n}.": "على الأكثر {n}.",
  "Choose one of the listed values.": "اختر إحدى القيم المدرجة.",
  "{type} details": "تفاصيل {type}",
  "Every change to the record, with who made it, and the work, defects and notes on it — newest first.":
    "كل تغيير على السجل مع من أجراه، والأعمال والعيوب والملاحظات عليه — الأحدث أولاً.",
  "by {who}": "بواسطة {who}",
  "Nothing is recorded against this asset yet.": "لا يوجد شيء مسجل على هذا الأصل بعد.",
  "Kept for good: a note is never edited or deleted.": "تُحفظ نهائياً: لا تُعدَّل الملاحظة ولا تُحذف.",
  "No notes on this asset yet.": "لا توجد ملاحظات على هذا الأصل بعد.",
  "Add a note": "إضافة ملاحظة",
  "What did you see, do or recommend?": "ماذا رأيت أو فعلت أو توصي به؟",
  "Choose the asset's type.": "اختر نوع الأصل.",
  "A tag is needed; it is how the asset is found.": "الرمز مطلوب؛ فهو ما يُعثر به على الأصل.",
  "This tag is already used on this site.": "هذا الرمز مستخدم بالفعل في هذا الموقع.",
  "No asset on this site has that tag.": "لا يوجد أصل في هذا الموقع بهذا الرمز.",
  "An asset cannot be its own parent.": "لا يمكن أن يكون الأصل أصلاً أباً لنفسه.",
  "Register a new asset": "تسجيل أصل جديد",
  "e.g. PMP-014": "مثال: PMP-014",
  "No zone": "بلا منطقة",
  "Make and model": "الصنع والطراز",
  "Dates and value": "التواريخ والقيمة",
  "Register the asset": "سجّل الأصل",
  "Save changes": "حفظ التغييرات",
  "Check the fields marked above.": "راجع الحقول المشار إليها أعلاه.",
  Choose: "اختر",
  Registered: "سُجِّل",
  Changed: "عُدِّل",
  "Work completed": "أُنجز العمل",
  "Defect found": "رُصد عيب",
  "Defect resolved": "عولج العيب",
  High: "عالية",
  "Parent asset": "الأصل الأب",
  "Part of (parent asset tag)": "جزء من (رمز الأصل الأب)",
  "Kind of note": "نوع الملاحظة",
};
export default ar;
